/**
 * DeviceMarker — Sprint 4.1.2 Hotfix #2.
 *
 * Marker = bolinha simples via L.divIcon (HTML <div> com CSS).
 * NÃO usa PNG/SVG customizado, NÃO usa ícones de bibliotecas externas.
 *
 * Cor segue prioridade lógica:
 *
 *   1. OFFLINE                          → vermelho (.marker-offline)
 *   2. ONLINE + OTA/comando em curso    → amarelo  (.marker-action)
 *   3. ONLINE + relay = 'off'           → laranja  (.marker-relay-off)
 *   4. ONLINE + relay = 'on' (ou null)  → verde    (.marker-online)
 *
 * RSSI fraco (`weak_signal=true` calculado no backend) adiciona classe
 * extra `.marker-weak` que renderiza halo amarelo SEM substituir a cor
 * base — só destaca o problema de sinal.
 *
 * "Ação em execução" = ota_state ∈ {pending, delivered, downloading, flashing}.
 * Limitação: como o briefing veta alterar o backend, NÃO inspeciono fila
 * de commands genéricos (relay_on / relay_off / reboot) — apenas OTA
 * states. Os 4 estados estão listados para forward-compatibility com
 * firmware futuro; hoje só pending e delivered ocorrem no backend.
 *
 * Outros estados visuais:
 *   selected   → borda ciano + tamanho 22px (override props.selected)
 *   draggable  → borda branca pulsando (override props.draggable)
 *
 * Compatibilidade:
 *   - Mantém assinatura createDeviceMarker(L, device, opts) idêntica.
 *   - Etapa 4.1: popup grande removido; mantém apenas bindTooltip (hover).
 *   - Mantém export deviceStatus() legado (usado por FleetFilters.jsx
 *     na lista lateral).
 */


import { getProtectionState, protectionSourceLabel } from "../../lib/protection.js";

const OTA_IN_PROGRESS = new Set([
  "pending", "delivered", "downloading", "flashing",
]);

/**
 * Status visual completo para colorir o marker.
 *
 * @returns {{variant: 'online'|'relay-off'|'offline'|'action', weak: boolean, tooltip: string}}
 */
export function deviceStatusFull(d) {
  // (1) offline domina tudo
  if (!d.online) {
    return { variant: "offline", weak: false, tooltip: "OFFLINE" };
  }
  // (2) ação em execução (sobre ON ou OFF do relé)
  if (d.ota_state && OTA_IN_PROGRESS.has(d.ota_state)) {
    return {
      variant: "action",
      weak: !!d.weak_signal,
      tooltip: "AÇÃO EM EXECUÇÃO",
    };
  }
  // (3) online + relay off
  if (d.relay === "off") {
    return {
      variant: "relay-off",
      weak: !!d.weak_signal,
      tooltip: "ONLINE · RELÉ OFF",
    };
  }
  // (4) online + relay on (ou desconhecido — default green)
  return {
    variant: "online",
    weak: !!d.weak_signal,
    tooltip: d.relay === "on" ? "ONLINE · RELÉ ON" : "ONLINE",
  };
}

/**
 * Sprint 5.3 — classe de saúde para o marcador.
 *
 * Quando o device tem device_health, a saúde operacional dá a COR
 * do marcador (sobrepõe a cor de online/relay):
 *   healthy  → verde      warning  → amarelo
 *   critical → vermelho   offline  → cinza
 * Sem health_status → "" (mantém a cor padrão de online/offline).
 *
 * Combina com o anel de inventory (S5.2D): a cor vem da saúde, o
 * anel vem do estoque — as duas informações coexistem no marcador.
 *
 * @param {string|null|undefined} healthStatus
 * @returns {string}
 */
export function healthClass(healthStatus) {
  if (healthStatus === "healthy") return "marker-health-healthy";
  if (healthStatus === "warning") return "marker-health-warning";
  if (healthStatus === "critical") return "marker-health-critical";
  if (healthStatus === "offline") return "marker-health-offline";
  return "";
}

/**
 * M1.2 — Health Map: classe de cor do marcador por health_score (0-100).
 * Faixas: 90-100 verde (glow), 70-89 amarelo, 50-69 laranja, 0-49 vermelho,
 * offline cinza. O `health_score` é injetado client-side no MapView (join
 * com /api/admin/health — sem backend novo). Sem score → fallback para a
 * cor por health_status (comportamento anterior).
 */
export function scoreClass(device) {
  // Prioridade visual do marcador (regra operacional):
  //   1. Offline            → cinza
  //   2. Relé OFF (online)  → laranja  (mesmo com health_score 100)
  //   3. RSSI fraco         → amarelo
  //   4. Health crítico     → vermelho
  //   5. Health atenção     → laranja
  //   6. Online saudável    → verde
  // Campo `relay` ("on"/"off") já vem do DTO do mapa.
  if (!device.online || device.health_status === "offline") return "marker-score-offline";
  if (device.relay === "off") return "marker-score-relayoff";   // 2 — laranja
  if (device.weak_signal) return "marker-score-weak";           // 3 — amarelo
  const s = device.health_score;
  if (s == null) return healthClass(device.health_status);      // fallback
  if (s >= 90) return "marker-score-excellent";
  if (s >= 70) return "marker-score-good";
  if (s >= 50) return "marker-score-warning";
  return "marker-score-critical";
}

function _esc(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// MAP-HEALTH-1B MICRO-UX — tooltip de hover COMPACTO (sem scroll). Cada linha
// é texto JÁ escapado pelo caller (segmentos via _esc); cls opcional
// ("warn"/"err") só colore. "" quando vazio (omitida).
function _tipLine(text, cls) {
  if (!text) return "";
  return `<div class="ipwd-mk-tip-line${cls ? " " + cls : ""}">${text}</div>`;
}
function _tipGroup(title, rows) {
  const body = rows.filter(Boolean).join("");
  if (!body) return "";
  const t = title ? `<div class="ipwd-mk-tip-gtitle">${_esc(title)}</div>` : "";
  return `<div class="ipwd-mk-tip-grp">${t}${body}</div>`;
}
function _fmtUptime(s) {
  if (s == null) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  const d = Math.floor(n / 86400);
  const h = Math.floor((n % 86400) / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function _fmtSeen(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 0) return "agora";
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return `${Math.floor(diff / 86400)} d`;
}

/**
 * MAP-HEALTH-1B MICRO-UX — Tooltip COMPACTO de hover (poucas linhas, SEM
 * scroll). Resumo: cabeçalho (cliente · device_id · 🔒) · Operacional
 * (status·relé / rssi·visto / uptime) · Saúde+FW (reboots 24h · boots / FW).
 * Tooltip de hover SOME ao tirar o mouse, então não pode ter scroll interno —
 * detalhes longos (Segurança, Scheduler, OTA, último boot exato) vivem no
 * painel lateral/mobile. Tudo lido SÓ de `device`; valores escapados via _esc.
 */
export function markerTooltipHtml(device) {
  const offline = !device.online || device.health_status === "offline";

  // Cabeçalho: cliente (se houver) + device_id + cadeado se protegido.
  const cust = device.customer_name ? `${_esc(device.customer_name)} · ` : "";
  // PROTECTED-DEVICES-MANAGE-1E — proteção EFETIVA (ENV OR DB) + rótulo da origem.
  const _prot = getProtectionState(device);
  const _protLabel = protectionSourceLabel(_prot.source);
  const lock = _prot.protected
    ? ` <span class="ipwd-mk-tip-lock" title="${_esc(_protLabel || "Protegido")}">🔒</span>`
    : "";
  const head = `<div class="ipwd-mk-tip-id">${cust}${_esc(device.device_id)}${lock}</div>`;

  // Operacional compacto — linhas combinadas (status·relé / rssi·visto / uptime).
  const statusTxt = offline
    ? "Offline" : (device.weak_signal ? "Sinal fraco" : "Online");
  const relayTxt = device.relay == null
    ? null : (device.relay === "on" ? "Relé ligado" : "Relé desligado");
  const line1 = [statusTxt, relayTxt].filter(Boolean).map((t) => _esc(t)).join(" · ");
  const seen = _fmtSeen(device.last_seen);
  const line2 = [
    device.rssi != null ? `RSSI ${_esc(device.rssi)} dBm` : null,
    seen ? `Visto ${_esc(seen)}` : null,
  ].filter(Boolean).join(" · ");
  const up = _fmtUptime(device.uptime_s);
  const opGrp = _tipGroup("", [
    _tipLine(line1, offline ? "err" : (device.weak_signal ? "warn" : null)),
    _tipLine(line2),
    up ? _tipLine(`Uptime ~${_esc(up)}`) : "",
  ]);

  // Saúde recente em 1 linha + firmware compacto. Watchdog 24h só aparece > 0.
  // 0 reboots = neutro (sem cor de alerta); boots é histórico (sempre neutro).
  const rb24 = device.reboot_count_24h;
  const wd24 = device.watchdog_count_24h;
  const healthParts = [
    rb24 != null ? `Reboots 24h: ${_esc(rb24)}` : null,
    (wd24 != null && wd24 > 0) ? `WD 24h: ${_esc(wd24)}` : null,
    device.boot_count != null ? `Boots: ${_esc(device.boot_count)}` : null,
  ].filter(Boolean);
  const healthCls = (rb24 > 0 || wd24 > 0) ? "warn" : null;
  const tgt = device.target_release_version;
  const fwStale = !!(device.fw_version && tgt && device.fw_version !== tgt);
  const fwLine = device.fw_version
    ? `FW ${_esc(device.fw_version)}${fwStale ? ` → ${_esc(tgt)}` : ""}`
    : null;
  const btmGrp = _tipGroup("", [
    healthParts.length ? _tipLine(healthParts.join(" · "), healthCls) : "",
    fwLine ? _tipLine(fwLine, fwStale ? "warn" : null) : "",
  ]);

  return `<div class="ipwd-mk-tip">${head}${opGrp}${btmGrp}</div>`;
}

/**
 * Sprint 5.5 — classe de status de Ordem de Serviço para o marcador.
 *
 * Quando o device tem uma OS (work_orders), um pequeno ponto no canto
 * do marcador indica o estado da tarefa de campo:
 *   open/scheduled → pendente (azul)
 *   in_progress    → andamento (roxo)
 *   installed      → instalado (sem ponto — a saúde já cobre)
 * cancelled ou sem OS → "" (sem ponto).
 *
 * É um TERCEIRO indicador, independente de healthClass (cor) e
 * inventoryRingClass (anel) — os três coexistem no marcador.
 *
 * @param {string|null|undefined} woStatus
 * @returns {string}
 */
export function workOrderClass(woStatus) {
  if (woStatus === "open" || woStatus === "scheduled") {
    return "marker-wo-pending";
  }
  if (woStatus === "in_progress") return "marker-wo-progress";
  return "";
}

/**
 * Sprint 5.2D — classe de anel de estoque para o marcador.
 *
 * Quando o device tem um item correspondente em device_inventory,
 * o marcador ganha um anel colorido em volta da bolinha de status:
 *   reserved → âmbar (instalação prevista, ainda não confirmada)
 *   installed → esmeralda (instalado e na operação)
 * Outros status (stock/maintenance/lost) ou ausência de inventory →
 * sem anel (string vazia).
 *
 * Não substitui a cor de online/offline do marcador — é aditivo.
 *
 * @param {string|null|undefined} invStatus
 * @returns {string}
 */
export function inventoryRingClass(invStatus) {
  if (invStatus === "reserved") return "marker-inv-reserved";
  if (invStatus === "installed") return "marker-inv-installed";
  return "";
}

/**
 * MAP-MANAGE-1C — device protegido contra ações críticas (denylist
 * OTA_PROTECTED_DEVICE_IDS). Classe aditiva (não substitui cor de status) +
 * badge de cadeado no canto. Backend é a fonte da verdade; aqui só comunica.
 */
export function protectedClass(otaProtected) {
  return otaProtected ? "marker-protected" : "";
}

/**
 * Badge 🔒 no canto superior-esquerdo do marcador quando protegido.
 * Inline-styled p/ não depender de CSS novo (parent .ipwd-marker é relative,
 * como o ponto de OS). pointer-events:none p/ não atrapalhar o clique.
 */
export function lockBadgeHtml(device) {
  if (!getProtectionState(device).protected) return "";
  return '<span class="ipwd-lock-dot" style="position:absolute;top:-7px;left:-7px;font-size:11px;line-height:1;pointer-events:none;text-shadow:0 0 2px #000">🔒</span>';
}

/**
 * MAP-UX-1A — anel tracejado âmbar quando o firmware está fora do alvo
 * (fw_version ≠ target_release_version, ambos presentes). target_release_version
 * é mesclado por device no MapView a partir do envelope do DTO. Aditivo; perde
 * para os anéis de proteção/estoque (mesma propriedade outline, ordem no CSS).
 */
export function fwStaleClass(device) {
  const tgt = device.target_release_version;
  if (device.fw_version && tgt && device.fw_version !== tgt) {
    return "marker-fw-stale";
  }
  return "";
}

/**
 * MAP-UX-1A — UM badge de atenção consolidado no canto inferior-direito,
 * para não poluir o marcador com vários glifos. Severidade:
 *   crit (vermelho): HMAC inseguro (secure_channel=false E auth_secret_set=true,
 *                    i.e. configurado mas em claro) OU clone suspeito.
 *   warn (âmbar):    watchdog desligado OU OTA pendente.
 * O detalhe de QUAL alerta vai no tooltip. "" = sem alerta.
 */
export function attentionLevel(device) {
  const crit =
    (device.secure_channel === false && device.auth_secret_set === true) ||
    device.clone_suspected === true;
  if (crit) return "crit";
  const warn =
    device.watchdog_enabled === false || device.has_pending_ota === true;
  if (warn) return "warn";
  return "";
}
export function attentionBadgeHtml(device) {
  const lvl = attentionLevel(device);
  if (!lvl) return "";
  return `<span class="ipwd-attn-dot attn-${lvl}"></span>`;
}

/**
 * deviceStatus legado — retorna 'online'|'weak'|'offline'.
 * MANTIDO para o FleetFilters.jsx que usa StatusDot na lista lateral.
 * Não usar para colorir marker do mapa; usar deviceStatusFull().
 */
export function deviceStatus(d) {
  if (!d.online) return "offline";
  if (d.weak_signal) return "weak";
  return "online";
}

/**
 * Cria um L.marker para um device com lat/lng cadastrados.
 *
 * @param {object} L            Leaflet instance
 * @param {object} device       FleetMapDeviceOut DTO
 * @param {object} opts
 *  @param {boolean} opts.selected
 *  @param {boolean} opts.draggable
 *  @param {function} opts.onClick({device, ctrlKey, metaKey})
 *  @param {function} opts.onDragEnd({device, lat, lng})
 * @returns {L.Marker | null}   null se device sem coordenadas
 */
export function createDeviceMarker(L, device, opts = {}) {
  const { selected, draggable, onClick, onDragEnd, focused } = opts;
  if (device.lat == null || device.lng == null) return null;

  const status = deviceStatusFull(device);

  // Classes CSS aplicadas ao <div> interno
  const classes = [
    "ipwd-marker",
    `marker-${status.variant}`,
    status.weak ? "marker-weak" : "",
    selected ? "is-selected" : "",
    focused ? "is-focused" : "",         // P1-4 — destaque do device focado
    draggable ? "is-dragging" : "",
    // Sprint 5.2D — anel de status de estoque, quando o device está
    // na camada de inventory. reserved/pending → âmbar; installed →
    // esmeralda. Sem inventory_status, nenhuma classe extra.
    inventoryRingClass(device.inventory_status),
    protectedClass(getProtectionState(device).protected), // MAP-MANAGE-1C + 1E (efetivo)
    fwStaleClass(device),                 // MAP-UX-1A — fw fora do alvo (anel tracejado)
    scoreClass(device),                  // M1.2 — cor por health_score (fallback: health_status)
  ].filter(Boolean).join(" ");

  // HTML do divIcon — bolinha CSS + (S5.5) ponto de OS + (1C) cadeado +
  // (MAP-UX-1A) badge de atenção consolidado (HMAC/clone/watchdog/OTA pend.).
  const woCls = workOrderClass(device.work_order_status);
  const woDot = woCls ? `<span class="ipwd-wo-dot ${woCls}"></span>` : "";
  const html = `<div class="${classes}">${woDot}${lockBadgeHtml(device)}${attentionBadgeHtml(device)}</div>`;

  const icon = L.divIcon({
    className: "ipwd-marker-wrap",   // wrapper sem estilo (CSS reset)
    html,
    iconSize: [22, 22],              // espaço suficiente para selected (22px)
    iconAnchor: [11, 11],            // centro do divIcon
  });

  const marker = L.marker([device.lat, device.lng], {
    icon,
    draggable: !!draggable,
    zIndexOffset: selected ? 1000 : (status.variant === "action" ? 500 : 0),
    riseOnHover: true,
  });

  // M1.2 — Tooltip rápida no hover (device_id · score · status · RSSI).
  marker.bindTooltip(markerTooltipHtml(device), {
    direction: "top",
    offset: [0, -10],
    opacity: 1,
    className: "ipwd-marker-tooltip",
  });

  // Etapa 4.1 — POPUP GRANDE REMOVIDO. O detalhe do device agora vive no
  // painel direito (desktop) e no bottom-sheet (mobile). Mantemos apenas a
  // tooltip de hover. Clicar no marker só dispara onClick → foca o device.

  if (onClick) {
    marker.on("click", (e) => {
      const orig = e.originalEvent || {};
      onClick({
        device,
        ctrlKey: !!orig.ctrlKey,
        metaKey: !!orig.metaKey,
      });
    });
  }

  if (onDragEnd && draggable) {
    marker.on("dragend", (e) => {
      const { lat, lng } = e.target.getLatLng();
      onDragEnd({ device, lat, lng });
    });
  }

  return marker;
}

/**
 * COLOR — mapa de cores para reuso eventual (badge na lista lateral, etc).
 * Mantido como antes para não quebrar import indireto.
 */
export const COLOR = {
  online: "#22c55e",
  weak: "#FFB224",
  offline: "#FF5063",
};

/**
 * Sprint 4.2A — Atualiza um marker JÁ existente in-place.
 *
 * Chamado em vez de remove+create durante auto-refresh. Preserva o
 * marker como objeto JS (e portanto seu popup aberto, hover, dragging
 * handler ligado em onClick/onDragEnd).
 *
 * Atualiza:
 *   - posição (lat/lng)
 *   - ícone (classes CSS conforme novo status)
 *   - tooltip (texto pode ter mudado)
 *   - popup content (HTML refletindo dados frescos)
 *   - z-index conforme variant
 *   - draggable state (se selected/draggable mudaram)
 */
export function updateDeviceMarker(L, marker, device, opts = {}) {
  const { selected, draggable, focused } = opts;
  if (!marker || device.lat == null || device.lng == null) return marker;

  const status = deviceStatusFull(device);

  // 1. Posição
  marker.setLatLng([device.lat, device.lng]);

  // 2. Ícone (classes refletem cor + selected/focused/draggable/weak)
  const classes = [
    "ipwd-marker",
    `marker-${status.variant}`,
    status.weak ? "marker-weak" : "",
    selected ? "is-selected" : "",
    focused ? "is-focused" : "",                   // P1-4 — device focado
    draggable ? "is-dragging" : "",
    inventoryRingClass(device.inventory_status),   // Sprint 5.2D
    protectedClass(getProtectionState(device).protected),  // MAP-MANAGE-1C + 1E (efetivo)
    fwStaleClass(device),                          // MAP-UX-1A — fw fora do alvo
    scoreClass(device),                            // M1.2 — cor por health_score
  ].filter(Boolean).join(" ");

  // Sprint 5.5 — ponto de OS + (1C) cadeado + (MAP-UX-1A) badge de atenção.
  const woCls = workOrderClass(device.work_order_status);
  const woDot = woCls ? `<span class="ipwd-wo-dot ${woCls}"></span>` : "";

  marker.setIcon(L.divIcon({
    className: "ipwd-marker-wrap",
    html: `<div class="${classes}">${woDot}${lockBadgeHtml(device)}${attentionBadgeHtml(device)}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  }));

  // 3. Tooltip — M1.2: HTML rápido (id·score·status·rssi); re-bind se mudou
  const newTooltip = markerTooltipHtml(device);
  const tipObj = marker.getTooltip?.();
  if (!tipObj || tipObj.getContent?.() !== newTooltip) {
    marker.unbindTooltip();
    marker.bindTooltip(newTooltip, {
      direction: "top",
      offset: [0, -10],
      opacity: 1,
      className: "ipwd-marker-tooltip",
    });
  }

  // 4. (Popup removido na Etapa 4.1 — detalhe vive no painel/sheet.)

  // 5. Z-index
  marker.setZIndexOffset(
    selected ? 1000 : (status.variant === "action" ? 500 : 0)
  );

  // 6. Draggable state — toggle dynamicamente
  if (marker.dragging) {
    if (draggable && !marker.dragging.enabled()) {
      marker.dragging.enable();
    } else if (!draggable && marker.dragging.enabled()) {
      marker.dragging.disable();
    }
  }

  return marker;
}
